import React, { useState } from 'react';
import { apiService } from '../services/apiService';
import type { FeedInspectResult } from '../services/apiService';
import { FEED_MAP_FIELDS, isRequiredFor, missingRequiredFields } from '../utils/feedColumns';

// Connect a feed by URL, with its columns confirmed before it is connected.
//
// Extracted from `FeedsManager`, whose list half became the shop list. The
// creation half had to survive the move: a scheduled sync has nobody to ask, so
// the column mapping is confirmed HERE, against a sample fetched from the URL,
// before the feed exists at all.
//
// IT NO LONGER OPENS CLOSED, AND THE COMMENT THAT SAID IT SHOULD HAD EXPIRED.
// This was a diagnostic path inside `/feeds`'s Advanced disclosure, so a
// collapsed panel with a `+ Add feed by URL` button was right: it was one of
// several things on a crowded page. #290 gave it its own route, reached by a
// named door from the hub, and nothing removed the second door underneath the
// first. Driven at 390 on 2026-09-07, `/feeds/connect` rendered 211 characters
// and ONE control — a route whose entire content is this form, opening with the
// form hidden, after the publisher had already chosen the task.
//
// `ConnectFeedPage` is its only consumer, so the collapsed state was not
// serving anyone else either. Gone with it: the `+ Add feed by URL` button, the
// dashed border that framed this as an inset panel on somebody else's page, and
// the `Add a feed by URL` heading that restated the page's own `h2` two lines
// above it. `Cancel` goes too — there is nothing left to cancel back to, and the
// route already carries `← Your shops` at constant depth, which is the promise
// the contract actually makes.

const fieldLabelStyle: React.CSSProperties = {
  fontSize: '0.8rem', fontWeight: 600, color: 'var(--ink-soft)', marginBottom: '-0.25rem',
};

const textInputStyle: React.CSSProperties = {
  padding: '0.4rem', border: '1px solid var(--line)', borderRadius: 'var(--radius)',
};

/** Check and Create are the same button in two steps of one flow. */
const submitStyle = (working: boolean): React.CSSProperties => ({ cursor: working ? 'wait' : 'pointer' });

const NETWORKS = [
  { value: 'amazon', label: 'Amazon-format CSV (prepared separately)' },
  { value: 'generic', label: 'Generic CSV (required fields)' },
  { value: 'shareasale', label: 'ShareASale legacy export (network closed)' },
  { value: 'cj', label: 'Commission Junction (CJ)' },
  { value: '2performant', label: '2Performant' },
  { value: 'awin', label: 'Awin' },
  { value: 'rakuten', label: 'Rakuten Advertising' },
  { value: 'impact', label: 'Impact' },
];

interface Props {
  onFeedCreated?: () => void;
}

const AddFeedByUrl: React.FC<Props> = ({ onFeedCreated }) => {
  const [network, setNetwork] = useState('');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [creating, setCreating] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [inspection, setInspection] = useState<FeedInspectResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [linkConfirmed, setLinkConfirmed] = useState(false);

  const resetReview = () => {
    setInspection(null);
    setMapping({});
    setLinkConfirmed(false);
  };

  /**
   * A CHECK BELONGS TO THE URL AND NETWORK IT WAS RUN FOR.
   *
   * Editing either after checking used to leave the review standing, and the
   * review is the only thing that ever sets this feed's `column_mapping`:
   * `createUrlBackedFeed` writes it once, `updateFeedSync` does not touch it and
   * no endpoint changes it. An upload can be repeated with a corrected mapping;
   * a URL feed cannot.
   *
   * Driven 2026-09-07: check `SHOP-A` as 2Performant, then change the URL to
   * `SHOP-B` and the network to Awin. The panel stayed, no re-check ran, the
   * dropdowns still offered SHOP-A's headers, the affiliate-link sample was still
   * a 2Performant tracking URL — and Create was enabled. `handleCreate` sends the
   * CURRENT network and URL with that stale mapping, so the publisher would have
   * persisted, permanently, a column mapping read off a different feed. The
   * "this is my tracked affiliate link" tick was made against the other one too.
   *
   * The upload form has always guarded its half of this — changing the network
   * there clears the inspection and re-inspects, because it still holds the File.
   * A URL cannot be re-fetched on every keystroke, so the review is dropped and
   * the flow returns to its Check step, which is the honest state.
   */
  const invalidateReview = () => { if (inspection) resetReview(); };

  const missingRequired = inspection ? missingRequiredFields(mapping, network) : [];

  // WHY THE BUTTON IS UNAVAILABLE — ONE LIST, WHICH IS BOTH THE GATE AND THE
  // REASON. The same shape `FeedUploadForm` got in #300, and copying it here is
  // also what stops this pair of components drifting for a fourth time.
  //
  // Check and Create are two steps of one flow and only one renders at a time,
  // so this is one list with a step-aware tail rather than two lists repeating
  // the first two clauses. It preserves both original conditions exactly:
  //
  //   check step   checking || !network || !url.trim()
  //   create step  creating || !network || !url.trim()
  //                  || missingRequired.length > 0 || !linkConfirmed
  //
  // Two entries say nothing because the screen already has words for them: the
  // busy one relabels its own button ("Checking…" / "Creating…"), and the
  // unmapped-column one is spelled out where the columns are ("Still needed: …").
  // A second sentence under the button repeating either is the "state it twice"
  // mistake in miniature.
  const blockers: Array<{ when: boolean; say: string | null }> = [
    { when: !network, say: 'Choose the affiliate network this feed came from.' },
    { when: !url.trim(), say: 'Enter the address your feed is published at.' },
    { when: checking || creating, say: null },
    ...(inspection ? [
      { when: missingRequired.length > 0, say: null },
      { when: !linkConfirmed, say: 'Tick “This is my tracked affiliate link” above to create the feed.' },
    ] : []),
  ];
  const blocking = blockers.find(b => b.when) ?? null;

  const handleCheck = async () => {
    if (!network || !url.trim()) return;
    setError(''); setNotice(''); setChecking(true);
    resetReview();
    try {
      const found = await apiService.inspectFeedUrl(network, url.trim());
      setInspection(found);
      setMapping({ ...(found.mapping || {}) });
    } catch (err) {
      // A URL we cannot read now is a URL the scheduler could not read either,
      // so surfacing it here is the whole point.
      setError(err instanceof Error ? err.message : 'Could not read that feed');
    } finally {
      setChecking(false);
    }
  };

  const handleCreate = async () => {
    if (!network || !url.trim()) return;
    setError(''); setNotice(''); setCreating(true);
    try {
      await apiService.createFeed(network, url.trim(), label.trim() || undefined, mapping);
      setNotice('Feed created — it will sync automatically within a few minutes, or use “Sync now” on its shop once it appears.');
      setNetwork(''); setLabel(''); setUrl('');
      resetReview();
      onFeedCreated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create feed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="create-feed-form">
      {/* Above the form rather than replacing it: the publisher may well connect
          a second feed, and the page's own `connect-done` link is what offers
          the way back. */}
      {notice && (
        <div data-testid="add-feed-notice" style={{ backgroundColor: 'var(--good-bg)', color: 'var(--good)', padding: '0.75rem', borderRadius: 'var(--radius)', marginBottom: '1rem' }}>
          {notice}
        </div>
      )}
      <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: 'var(--muted)' }}>
        No upload needed — products arrive automatically once we sync the URL.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '420px' }}>
        {/* Labels, not placeholders. A placeholder is the label only until you
            type; this select had no accessible name at all, and the two inputs
            lost theirs the moment they were filled in. */}
        <label htmlFor="create-feed-network" style={fieldLabelStyle}>Affiliate network</label>
        <select id="create-feed-network" data-testid="create-feed-network" value={network} onChange={e => { setNetwork(e.target.value); invalidateReview(); }} style={{ padding: '0.4rem' }}>
          <option value="">Select a network...</option>
          {NETWORKS.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
        </select>

        <label htmlFor="create-feed-label" style={fieldLabelStyle}>Label (optional)</label>
        <input
          id="create-feed-label"
          data-testid="create-feed-label"
          type="text"
          placeholder="advertiser name, e.g. aureli"
          value={label}
          onChange={e => setLabel(e.target.value)}
          style={textInputStyle}
        />

        <label htmlFor="create-feed-url" style={fieldLabelStyle}>Feed URL</label>
        <input
          id="create-feed-url"
          data-testid="create-feed-url"
          type="text"
          inputMode="url"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="https://example.com/feed.csv"
          value={url}
          onChange={e => { setUrl(e.target.value); invalidateReview(); }}
          style={textInputStyle}
        />

        {error && (
          <div data-testid="create-feed-error" role="alert" style={{ backgroundColor: 'var(--danger-bg)', color: 'var(--danger)', padding: '0.6rem 0.75rem', borderRadius: 'var(--radius)', fontSize: '0.875rem' }}>
            {error}
          </div>
        )}
        {checking && (
          <div data-testid="url-checking" style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>Reading your feed…</div>
        )}

        {inspection && (
          <div data-testid="url-column-review" style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: '0.7rem' }}>
            {/* When autoResolved this is a confirmation of ONE field, not a
                column-picking chore, and the old wording read as a failure even
                to the person who built it. */}
            <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
              {inspection.autoResolved ? 'Columns recognised' : 'Check your columns'}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
              {inspection.autoResolved
                ? 'We read your feed and matched every column automatically. Just confirm the affiliate link below — it is the one we always double-check, because the wrong column would send clicks that earn you nothing.'
                : 'We could not recognise every column. Pick the right one for anything marked below.'}
            </div>
            <table className="column-map" style={{ fontSize: '0.83rem' }}>
              <tbody>
                {FEED_MAP_FIELDS.map((field) => {
                  const { key, label: fieldLabel } = field;
                  const needed = isRequiredFor(field, network);
                  const chosen = mapping[key] || '';
                  if (!needed && !chosen) return null;
                  return (
                    <tr key={key} data-testid={`url-map-row-${key}`}>
                      <td>
                        {fieldLabel}{needed && <span style={{ color: 'var(--danger)' }}> *</span>}
                      </td>
                      <td>
                        <select
                          data-testid={`url-map-select-${key}`}
                          aria-label={fieldLabel}
                          value={chosen}
                          onChange={e => {
                            setMapping(prev => ({ ...prev, [key]: e.target.value }));
                            if (key === 'link') setLinkConfirmed(false);
                          }}
                        >
                          <option value="">— not set —</option>
                          {inspection.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </td>
                      <td className="column-map-sample"
                          title={inspection.samples?.[key] || ''}>
                        {chosen ? (inspection.samples?.[key] || '') : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <label style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.83rem' }}>
              <input
                type="checkbox"
                data-testid="url-confirm-link"
                checked={linkConfirmed}
                onChange={e => setLinkConfirmed(e.target.checked)}
              />{' '}
              This is my tracked affiliate link
              {mapping.link && inspection.samples?.link && (
                <span style={{ color: 'var(--muted)' }}> — {inspection.samples.link}</span>
              )}
            </label>
            {missingRequired.length > 0 && (
              <div data-testid="url-missing-required" style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--danger)' }}>
                Still needed: {missingRequired.map(f => f.label).join(', ')}
              </div>
            )}
          </div>
        )}

        <div>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {!inspection ? (
              <button
                data-testid="check-feed-url"
                onClick={handleCheck}
                disabled={blocking !== null}
                className="btn btn--primary" style={submitStyle(checking)}
              >
                {checking ? 'Checking…' : 'Check feed'}
              </button>
            ) : (
              <button
                data-testid="create-feed-submit"
                onClick={handleCreate}
                disabled={blocking !== null}
                className="btn btn--primary" style={submitStyle(creating)}
              >
                {creating ? 'Creating…' : 'Create feed'}
              </button>
            )}
          </div>
          {blocking?.say && (
            <p
              data-testid="create-feed-blocked"
              role="status"
              style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: 'var(--muted)' }}
            >
              {blocking.say}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddFeedByUrl;
